/**
 * Self-service signup API — the backend contracts the /get-started flow consumes.
 *
 *   GET  /api/waitlist/self-service/status/    → server-driven gate + Turnstile site key
 *   POST /api/waitlist/self-service/start/     → Turnstile token → arms the session for OAuth
 *   GET  /api/onboarding/self/team-suggestions/ → teammates from the signed-in user's calendar
 *   POST /api/waitlist/self-service/confirm/   → wizard payload → org + Stripe Checkout URL
 *
 * Plain React Query hooks over `apiGet`/`apiPost` (FRONTEND_PATTERNS § Pattern B).
 */
import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';
import { apiGet, apiPost } from '@/lib/http/client';
import type { BillingPeriod, TeamSize } from '@/lib/self-service/constants';
import type { SelfServiceFlowState } from '@/lib/self-service/flowState';

export const SELF_SERVICE_STATUS_PATH = '/api/waitlist/self-service/status/';
export const SELF_SERVICE_START_PATH = '/api/waitlist/self-service/start/';
export const SELF_SERVICE_PROVISION_PATH =
  '/api/waitlist/self-service/provision/';
export const SELF_SERVICE_CONFIRM_PATH = '/api/waitlist/self-service/confirm/';
export const TEAM_SUGGESTIONS_PATH = '/api/onboarding/self/team-suggestions/';

export interface SelfServiceStatus {
  enabled: boolean;
  /** Cloudflare Turnstile site key; null when captcha is not configured (dev/QA). */
  turnstile_site_key: string | null;
}

export interface TeamSuggestion {
  email: string;
  name: string;
  score: number;
  domain_match: boolean;
  email_interactions: number;
  meeting_count: number;
  total_interactions: number;
}

export interface TeamSuggestionsResponse {
  suggestions: TeamSuggestion[];
  total_count: number;
}

export interface SelfServiceProvisionPayload {
  company_name: string;
  team_size: TeamSize;
  role: string;
}

export interface SelfServiceProvisionResponse {
  org_id: number;
}

export interface SelfServiceConfirmPayload {
  company_name: string;
  team_size: TeamSize;
  role: string;
  billing_period: BillingPeriod;
  data_sources: string[];
  recorders: string[];
  invites: { email: string }[];
}

export interface SelfServiceConfirmResponse {
  org_id: number;
  checkout_url: string;
}

export const selfServiceQueryKeys = {
  status: ['self-service', 'status'] as const,
  teamSuggestions: ['self-service', 'team-suggestions'] as const,
};

/** Server-driven gate. Immutable for the life of the page — never refetched. */
export function useSelfServiceStatus() {
  return useQuery({
    queryKey: selfServiceQueryKeys.status,
    queryFn: () => apiGet<SelfServiceStatus>(SELF_SERVICE_STATUS_PATH),
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Arms the session for an invite-code-free OAuth signup. The token is omitted
 * from the body when Turnstile is not configured (the backend skips
 * verification in dev environments).
 */
export function useStartSelfServiceSignup() {
  return useMutation({
    mutationFn: (turnstileToken: string | null) =>
      apiPost<Record<string, never>>(
        SELF_SERVICE_START_PATH,
        turnstileToken ? { turnstile_token: turnstileToken } : {}
      ),
  });
}

/** Calendar-derived teammate suggestions for the signed-in, org-less user. */
export function useTeamSuggestions() {
  return useQuery({
    queryKey: selfServiceQueryKeys.teamSuggestions,
    queryFn: () => apiGet<TeamSuggestionsResponse>(TEAM_SUGGESTIONS_PATH),
    // Each fetch fans out to the user's live calendar; the list does not change
    // within a wizard session, so a step revisit must not refetch.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useProvisionSelfService() {
  return useMutation({
    mutationFn: (payload: SelfServiceProvisionPayload) =>
      apiPost<SelfServiceProvisionResponse>(
        SELF_SERVICE_PROVISION_PATH,
        payload
      ),
  });
}

export function useConfirmSelfService() {
  return useMutation({
    mutationFn: (payload: SelfServiceConfirmPayload) =>
      apiPost<SelfServiceConfirmResponse>(SELF_SERVICE_CONFIRM_PATH, payload),
  });
}

export type ConfirmPayloadResult =
  | { ok: true; payload: SelfServiceConfirmPayload }
  | { ok: false; missing: 'crew' };

/**
 * Map the wizard's flow state onto the confirm wire shape.
 *
 * The crew step is the only one with required fields (`company_name`,
 * `team_size`); a direct URL into the trial step can skip it, so the result is
 * a discriminated union rather than a throw — the trial step renders the
 * "finish step 1" hint from `missing`.
 */
export function buildConfirmPayload(
  state: SelfServiceFlowState
): ConfirmPayloadResult {
  const companyName = state.companyName.trim();
  if (!companyName || state.teamSize === null) {
    return { ok: false, missing: 'crew' };
  }

  const emails = new Set<string>();
  for (const raw of [...state.selectedTeammates, state.inviteEmail]) {
    const email = raw.trim().toLowerCase();
    if (email) {
      emails.add(email);
    }
  }

  return {
    ok: true,
    payload: {
      company_name: companyName,
      team_size: state.teamSize,
      role: state.role.trim(),
      billing_period: state.billingPeriod,
      data_sources: state.dataSources,
      recorders: state.recorders,
      invites: Array.from(emails, email => ({ email })),
    },
  };
}

/** Customer-facing copy for a failed confirm (never the raw server message). */
export function confirmErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.statusCode === 403) {
    return "This account can't start a trial here. Get in touch and we'll sort it out.";
  }
  return "We couldn't start your trial. Try again, and get in touch if it keeps happening.";
}
